/**
 * checkState -> pill tone + default mono label. The colour each tone resolves to is
 * fixed by the assurance spine: m=jade(verified), a=gold(attestation), f=brick(flag),
 * n=neutral(absence). Carried by colour PLUS label, never colour alone.
 */
export function checkStateToPill(state) {
    switch (state) {
        case 'verified_machine':
            return { tone: 'm', label: 'verified · machine' };
        case 'verified_attestation':
            return { tone: 'a', label: 'verified · attestation' };
        case 'flag':
            return { tone: 'f', label: 'flag' };
        case 'error':
            return { tone: 'f', label: 'error' };
        case 'unverified':
            return { tone: 'n', label: 'unverified' };
        case 'pending':
            return { tone: 'n', label: 'pending' };
    }
}
/** The single source of truth tying each pill tone to its assurance-spine hex. */
export const PILL_TONE_COLOR = {
    m: '#1b7a6b', // verified (jade)
    a: '#b6862c', // attestation (gold)
    f: '#b3402f', // flag (brick)
    n: '#8a8d93', // neutral / unverified
};
const QUAL = {
    clear: { cls: 'clear', label: 'clear' },
    flags: { cls: 'flags', label: 'clear with flags' },
    blocked: { cls: 'blocked', label: 'blocked' },
    expired: { cls: 'expired', label: 'expired' },
    verifying: { cls: 'expired', label: 'verifying' },
};
export function qualSpec(state) {
    return QUAL[state];
}
/** Map a domain RecordState onto the UI qualification value. */
export function recordStateToQual(state) {
    switch (state) {
        case 'clear':
            return 'clear';
        case 'clear_with_flags':
            return 'flags';
        case 'blocked':
            return 'blocked';
        case 'expired':
            return 'expired';
    }
}
// ─────────────────────────── TPM tier ramp (console) ───────────────────────────
/** Number of ramp ticks inked solid for a tier (1..4). Ordering is the criticality ramp. */
export const TIER_FILLED = {
    low: 1,
    standard: 2,
    high: 3,
    critical: 4,
};
/** Ascending criticality rank — low < standard < high < critical. */
export const TIER_ORDER = ['low', 'standard', 'high', 'critical'];
export function tierRank(tier) {
    return TIER_ORDER.indexOf(tier);
}
/** The four depths in fixed order; machine certainty falls left→right. */
export const DEPTHS = [
    { key: 'type_lawful', n: 'DEPTH 01', t: 'type-lawful' },
    { key: 'identity_authentic', n: 'DEPTH 02', t: 'identity-authentic' },
    { key: 'unit_authentic', n: 'DEPTH 03', t: 'unit-authentic' },
    { key: 'provenance_clean', n: 'DEPTH 04', t: 'provenance-clean' },
];
export function depthIndex(reached) {
    return DEPTHS.findIndex((d) => d.key === reached);
}
/** Thresholds match the prototype: >=85 high, >=60 mid, else low. */
export function ocrLevel(conf) {
    return conf >= 85 ? 'hi' : conf >= 60 ? 'mid' : 'lo';
}
const STATUS = {
    published: { cls: 'b-pass', label: 'Published', dot: 'var(--status-pass-text)' },
    in_review: { cls: 'b-info', label: 'In review', dot: 'var(--status-info-text)', pulse: true },
    draft: { cls: 'b-neutral', label: 'Draft', dot: 'var(--fg-3)' },
    superseded: { cls: 'b-neutral', label: 'Superseded', dot: 'var(--fg-3)' },
    retired: { cls: 'b-fail', label: 'Retired', dot: 'var(--status-fail-text)' },
};
export function statusSpec(status) {
    return STATUS[status];
}
const COVERAGE = {
    covered: { cls: 'b-pass', label: 'Covered', dot: 'var(--cov-covered)' },
    thin: { cls: 'b-warn', label: 'Thin', dot: 'var(--cov-thin)' },
    empty: { cls: 'b-neutral', label: 'Empty', dot: 'var(--fg-3)' },
};
export function coverageSpec(state) {
    return COVERAGE[state];
}
export function gateSpec(gate) {
    return gate === 'answer'
        ? { cls: 'b-pass', label: 'answer', dot: 'var(--status-pass-text)' }
        : { cls: 'b-abstain', label: 'abstain', dot: 'var(--abstain-text)' };
}
export function watchSpec(state) {
    switch (state) {
        case 'changed':
            return { cls: 'b-warn', label: 'changed', dot: 'var(--status-warn-text)', pulse: true };
        case 'monitoring':
            return { cls: 'b-info', label: 'monitoring', dot: 'var(--status-info-text)' };
        case 'stable':
            return { cls: 'b-neutral', label: 'stable', dot: 'var(--status-pass)' };
    }
}
/** Flag severity -> Core badge tone. low=neutral, medium=warn, high/blocking=fail. */
export function severitySpec(severity) {
    switch (severity) {
        case 'low':
            return { cls: 'b-neutral', label: 'low', dot: 'var(--fg-3)' };
        case 'medium':
            return { cls: 'b-warn', label: 'medium', dot: 'var(--status-warn-text)' };
        case 'high':
            return { cls: 'b-fail', label: 'high', dot: 'var(--status-fail-text)' };
        case 'blocking':
            return { cls: 'b-fail', label: 'blocking', dot: 'var(--status-fail-text)', pulse: true };
    }
}
// ─────────────────────────── small class helper ───────────────────────────
/** Join truthy class fragments; trims so empty fragments don't leave double spaces. */
export function cx(...parts) {
    return parts.filter(Boolean).join(' ');
}
//# sourceMappingURL=variants.js.map